import { Box } from '@cp/ds/src/components/box';
import { Stack } from '@cp/ds/src/components/stack';
import { Typography } from '@cp/ds/src/components/typography';
import { Link } from '@cp/ds/src/components/link';
import React from 'react';
import { styled } from '@cp/ds';
import { useTheme } from '@cp/ds/src/hooks/useTheme';
import babyblog from 'src/assets/shapes/media/babyblog.svg?url';
import tbs from 'src/assets/shapes/media/tbs.svg?url';
import pikabu from 'src/assets/shapes/media/pikabu.png';
import vc from 'src/assets/shapes/media/vc.svg?url';
import village from 'src/assets/shapes/media/village.svg?url';
import VectorHorizontal from 'src/assets/shapes/vectors/vectorHorizontal.svg';
import VectorVertical from 'src/assets/shapes/vectors/vectorVertical.svg';
import { Image } from '@youhelp-ru/ui';

const StyledItem = styled(Stack)(({ theme }) => ({
  alignItems: 'center',
  backgroundColor: 'white',
  borderRadius: '16px',
  height: '100px',
  justifyContent: 'center',
  [theme.breakpoints.up('md')]: {
    height: '187px',
  },
}));

const StyledMediaLink = styled(Link)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: '100%',
  width: '100%',
});

const StyledTitovalifeLink = styled(Link)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: theme.palette['gray light'],
  borderRadius: '16px',
  position: 'relative',
  height: '100px',
  width: '100%',
  textAlign: 'start',
  gap: '15px',
  padding: '26px 32px',
  [theme.breakpoints.up('md')]: {
    height: '187px',
  },
}));

type LogoSize = {
  width: number;
  height: number;
  widthMd: number;
  heightMd: number;
};

const defaultLogoSize: LogoSize = { width: 129, height: 40, widthMd: 245, heightMd: 60 };

const sources: { src: string; href?: string; size?: LogoSize }[] = [
  {
    src: pikabu,
    href: 'https://pikabu.ru/story/skolko_platit_nyane_v_2026_tut_realnyie_tsenyi_po_gorodam_13888480',
    size: { width: 150, height: 57, widthMd: 261, heightMd: 100 },
  },
  { src: vc, href: 'https://vc.ru/tribuna/2799010-kak-my-sozdali-servis-po-podboru-nyan-youhelp' },
  {
    src: babyblog,
    href: 'https://www.babyblog.ru/user/janelovesyou2623/12094',
    size: { width: 129, height: 21, widthMd: 188, heightMd: 31 },
  },
  { src: tbs, href: 'https://secrets.tbank.ru/blogi-kompanij/kak-sozdat-servis-s-nulya' },
  { src: village, size: { width: 129, height: 45, widthMd: 168, heightMd: 59 } },
  {
    src: 'https://cdn.youhelp.ru/img/workingmama.webp',
    href: 'https://workingmama.ru/news/2020/10/20/youhelp-kak-najti-nadyozhnyj-domashnij-personal/',
    size: { width: 130, height: 57, widthMd: 243, heightMd: 106 },
  },
];

const MediaLogo: React.FC<{ src: string; size: LogoSize }> = ({ src, size }) => (
  <Image
    alt="icon"
    height={size.height}
    loading="lazy"
    src={src}
    sx={{
      height: { xs: `${size.height}px`, md: `${size.heightMd}px` },
      maxWidth: '100%',
      width: { xs: `${size.width}px`, md: `${size.widthMd}px` },
    }}
    width={size.width}
  />
);

export const Media = ({ isClassic }: { isClassic?: boolean }) => {
  const theme = useTheme();

  const renderLogoItem = ({ src, href, size = defaultLogoSize }: (typeof sources)[number], key: React.Key) => {
    const logo = <MediaLogo size={size} src={src} />;

    return (
      <StyledItem key={key} sx={{ border: isClassic ? `1px solid ${theme.palette['gray middle']}` : 'none' }}>
        {href ? (
          <StyledMediaLink external href={href} rel="nofollow noopener noreferrer" underline={false}>
            {logo}
          </StyledMediaLink>
        ) : (
          logo
        )}
      </StyledItem>
    );
  };

  return (
    <Stack sx={{ gap: { xs: 1.5, md: 2.5 }, pb: 2.5 }}>
      <Box
        sx={{
          display: 'grid',
          gap: { xs: 1.5, md: 2.5 },
          gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(3, minmax(0, 1fr))' },
        }}
      >
        {sources.map((source, i) => renderLogoItem(source, i))}
      </Box>

      <StyledTitovalifeLink disableHover external href={'https://titovalife.ru/'} underline={false}>
        <Typography component="span" sx={{ typography: { xs: 'h3', md: 'h1' } }} variant="h3">
          Блог психолога педагога Монтессори titovalife.ru
        </Typography>
        <Typography component="span" sx={{ typography: { xs: 'h3', md: 'h1' } }} variant="h3">
          →
        </Typography>
        <Box
          sx={{
            display: { xs: 'none', md: 'block' },
            position: 'absolute',
            bottom: '-7px',
            right: '-1px',
            width: '5%',
            color: isClassic ? 'white' : 'gray ultra light',
            transform: 'scaleX(-1)',
          }}
        >
          <VectorVertical />
        </Box>
        <Box
          sx={{
            color: isClassic ? 'white' : 'gray ultra light',
            display: { xs: 'block', md: 'none' },
            position: 'absolute',
            bottom: '-2px',
            right: 0,
            width: '15%',
            transform: 'scale(-1)',
          }}
        >
          <VectorHorizontal />
        </Box>
      </StyledTitovalifeLink>
    </Stack>
  );
};
